import React, { useRef, useEffect, useState } from "react";
import { Text, Stack } from "@mantine/core";
import { IconSend } from "@tabler/icons-react";
import StandardModal from "../shared/Modal/StandardModal";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";

function SentDocumentsModal({ isOpen, onClose, recruit, onDocumentsSent }) {
  const confirmButtonRef = useRef(null);
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Clear any previous errors when modal opens
      setError(null);

      if (confirmButtonRef.current) {
        // Small delay to ensure modal is fully rendered
        const timer = setTimeout(() => {
          confirmButtonRef.current.focus();
        }, 100);

        return () => clearTimeout(timer);
      }
    }
  }, [isOpen]);

  const handleYes = async () => {
    if (!recruit || isProcessing) return;

    try {
      setIsProcessing(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(
        getApiEndpoint(`/v1/recruiting/leads/${recruit.id}/status`),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ status: "Offers Sent" }),
        }
      );

      if (response.ok) {
        showSuccess(`${recruit.name} has been moved to Offers Sent!`);
        if (onDocumentsSent) {
          onDocumentsSent(recruit);
        }
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to update lead status");
      }
    } catch (err) {
      setError("An error occurred while updating status");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNo = () => {
    onClose();
  };

  if (!recruit) return null;

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Document Sending Status"
      footerLeft={
        <button
          className="btn btn-md btn-secondary"
          onClick={handleNo}
          disabled={isProcessing}
        >
          No
        </button>
      }
      footerRight={
        <button
          ref={confirmButtonRef}
          className="btn btn-md btn-primary"
          onClick={handleYes}
          disabled={isProcessing}
        >
          {isProcessing ? "Processing..." : "Yes, Mark as Sent"}
        </button>
      }
    >
      <Stack align="center" gap="md" py="md">
        <div
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: "#fef3c7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconSend size={40} color="#f59e0b" />
        </div>

        <Stack align="center" gap="xs">
          <Text size="lg" fw={600} ta="center">
            Have you sent documents to {recruit.name}?
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            This will mark the lead as "Offers Sent" in the pipeline.
          </Text>
        </Stack>

        {error && (
          <div style={{ width: "100%" }}>
            <DisplayMessage type="error" message={error} />
          </div>
        )}
      </Stack>
    </StandardModal>
  );
}

export default SentDocumentsModal;
